/**
 * Offline Storage Service
 * Manages local storage of ticket resolutions when offline
 */

const STORAGE_KEY = 'offline_ticket_resolutions';
const SYNC_STATUS_KEY = 'sync_status';

class OfflineStorageService {
    constructor() {
        this.storageKey = STORAGE_KEY;
        this.syncStatusKey = SYNC_STATUS_KEY;
        this.initStorage();
    }

    initStorage() {
        if (!localStorage.getItem(this.storageKey)) {
            localStorage.setItem(this.storageKey, JSON.stringify([]));
        }
        if (!localStorage.getItem(this.syncStatusKey)) {
            localStorage.setItem(this.syncStatusKey, JSON.stringify({ lastSync: null, pending: 0 }));
        }
    }

    /**
     * Save ticket resolution data for offline sync
     * @param {Object} resolutionData - The ticket resolution data
     * @returns {string} - Unique ID for the stored resolution
     */
    saveResolution(resolutionData) {
        const resolutions = this.getPendingResolutions();
        const id = `offline_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

        const resolution = {
            id,
            ticketId: resolutionData.ticket_id,
            ticketLabel: resolutionData.ticket_label,
            assigneeId: resolutionData.assignee_id,
            rootCause: resolutionData.root_cause,
            ca: resolutionData.ca,
            pa: resolutionData.pa,
            capa: resolutionData.capa,
            attachmentIds: resolutionData.attachmentIds || [], // Store attachment IDs from IndexedDB
            attachmentCount: resolutionData.attachmentCount || 0,
            timestamp: new Date().toISOString(),
            status: 'pending',
            retryCount: 0,
        };

        resolutions.push(resolution);
        localStorage.setItem(this.storageKey, JSON.stringify(resolutions));
        this.updateSyncStatus();

        return id;
    }

    /**
     * Get all pending resolutions
     * @returns {Array} - Array of pending resolutions
     */
    getPendingResolutions() {
        try {
            const data = localStorage.getItem(this.storageKey);
            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error('Error reading pending resolutions:', error);
            return [];
        }
    }

    /**
     * Get pending resolutions count
     * @returns {number} - Count of pending resolutions
     */
    getPendingCount() {
        return this.getPendingResolutions().filter(r => r.status === 'pending').length;
    }

    /**
     * Mark resolution as synced
     * @param {string} id - Resolution ID
     */
    markAsSynced(id) {
        const resolutions = this.getPendingResolutions();
        const updated = resolutions.map(r =>
            r.id === id ? { ...r, status: 'synced', syncedAt: new Date().toISOString() } : r
        );
        localStorage.setItem(this.storageKey, JSON.stringify(updated));
        this.updateSyncStatus();
    }

    /**
     * Mark resolution as failed
     * @param {string} id - Resolution ID
     * @param {string} error - Error message
     */
    markAsFailed(id, error) {
        const resolutions = this.getPendingResolutions();
        const updated = resolutions.map(r =>
            r.id === id ? { ...r, status: 'failed', error, retryCount: r.retryCount + 1 } : r
        );
        localStorage.setItem(this.storageKey, JSON.stringify(updated));
        this.updateSyncStatus();
    }

    /**
     * Mark resolution as discarded (ticket was escalated/expired before sync)
     * @param {string} id - Resolution ID
     * @param {string} reason - Reason for discarding
     */
    markAsDiscarded(id, reason) {
        const resolutions = this.getPendingResolutions();
        const updated = resolutions.map(r =>
            r.id === id ? { ...r, status: 'discarded', discardReason: reason, discardedAt: new Date().toISOString() } : r
        );
        localStorage.setItem(this.storageKey, JSON.stringify(updated));
        this.updateSyncStatus();
    }

    /**
     * Remove synced/discarded resolutions older than specified days
     * @param {number} days - Number of days to keep synced/discarded records
     */
    cleanupSyncedResolutions(days = 7) {
        const resolutions = this.getPendingResolutions();
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);

        const filtered = resolutions.filter(r => {
            if (r.status === 'synced' && r.syncedAt) {
                return new Date(r.syncedAt) > cutoffDate;
            }
            if (r.status === 'discarded' && r.discardedAt) {
                return new Date(r.discardedAt) > cutoffDate;
            }
            return true; // Keep pending and failed
        });

        localStorage.setItem(this.storageKey, JSON.stringify(filtered));
    }

    /**
     * Update sync status metadata
     */
    updateSyncStatus() {
        const resolutions = this.getPendingResolutions();
        const pending = resolutions.filter(r => r.status === 'pending').length;

        const status = {
            lastSync: new Date().toISOString(),
            pending,
            total: resolutions.length,
        };

        localStorage.setItem(this.syncStatusKey, JSON.stringify(status));
    }

    /**
     * Get sync status
     * @returns {Object} - Sync status object
     */
    getSyncStatus() {
        try {
            const data = localStorage.getItem(this.syncStatusKey);
            return data ? JSON.parse(data) : { lastSync: null, pending: 0 };
        } catch (error) {
            console.error('Error reading sync status:', error);
            return { lastSync: null, pending: 0 };
        }
    }

    /**
     * Clear all offline data (use with caution)
     */
    clearAll() {
        localStorage.removeItem(this.storageKey);
        localStorage.removeItem(this.syncStatusKey);
        this.initStorage();
    }
}

export default new OfflineStorageService();
